import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Clock, AmazonLogo, ArrowRight } from '@phosphor-icons/react';

export const PurchaseSection = () => {
  const { t, countdown } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
    },
  };

  return (
    <section
      ref={ref}
      className="relative py-24 md:py-40 bg-[#2A2624] overflow-hidden"
      data-testid="purchase-section"
    >
      {/* Grain Texture */}
      <div 
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Decorative Line */}
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-px h-24 bg-gradient-to-b from-transparent to-[#B89C6A]/30" />

      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="text-center"
        >
          <motion.span
            variants={itemVariants}
            className="text-xs tracking-[0.25em] uppercase text-[#B89C6A] font-outfit font-semibold"
          >
            {countdown.purchaseOverline}
          </motion.span>
          
          <motion.h2
            variants={itemVariants}
            className="font-cormorant text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#FDFBF7] mt-4 mb-6"
          >
            {countdown.purchaseTitle}
          </motion.h2>

          <motion.p
            variants={itemVariants}
            className="font-outfit text-lg text-[#9A948C] mb-12 max-w-xl mx-auto"
          >
            {countdown.purchaseSubtitle}
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="flex flex-col items-center gap-6"
          >
            {countdown.isLive ? (
              <motion.a
                href={countdown.amazonLink}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group inline-flex items-center gap-4 bg-[#FDFBF7] text-[#2A2624] hover:bg-[#B89C6A] transition-colors duration-500 px-12 py-5 text-sm tracking-widest uppercase font-outfit"
                data-testid="purchase-buy-amazon"
              >
                <AmazonLogo size={24} weight="bold" />
                <span>{countdown.purchaseCta}</span>
                <ArrowRight 
                  size={18} 
                  className="transform group-hover:translate-x-1 transition-transform duration-300"
                />
              </motion.a>
            ) : (
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="inline-flex items-center gap-4 bg-[#FDFBF7] text-[#2A2624] px-12 py-5"
                data-testid="purchase-countdown"
              >
                <Clock size={24} weight="bold" className="text-[#B89C6A]" />
                <span className="font-cormorant text-3xl tracking-wide">{countdown.purchaseCta}</span>
              </motion.div>
            )}

            <motion.p
              variants={itemVariants}
              className="text-xs tracking-wide text-[#6B665F] font-outfit"
            >
              {countdown.purchaseNote}
            </motion.p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
